import useElement from "backend/hooks/elements/useElement";
import useTopElements from "backend/hooks/model/useTopElements";
import PeriodSelector from "components/PeriodSelector";
import StrategyReport from "components/reports/StrategyReport";
import DivisionReport from "components/reporttemplates/DivisionReport";
import ReportsToolbar from "components/toolbars/ReportsToolbar";
import LaunchReport from "design/components/buttons/LaunchReport";
import CollapseCard from "design/components/cards/CollapseCard";
import ScopeCard from "design/components/cards/ScopeCard";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import ExportFileSelectionPopUp from "design/components/popups/ExportFileSelectionPopup";
import SideBar from "design/components/toolbar/SideBar";
import ElementTreeView from "design/components/trees/ElementTreeView";
import RowView from "design/layouts/RowView";
import StackView from "design/layouts/StackView";
import TreeViewLeftPanel from "design/layouts/TreeViewLeftPanel";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import React, { FunctionComponent } from "react";
import { useSelectedReportContext } from "state/SelectedReportContext";
import Loader from "qpr-base/ui/components/Loader";
import { ICardElementWithChildren, ITopElement } from "backend/model/model";
import { buildTree } from "utils/utils";

interface ReportTemplatesProps {}
const ReportTemplates: FunctionComponent<ReportTemplatesProps> = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: topElems, isLoading: topElementsLoading } = useTopElements();
  const { data: rootItem } = useElement(ctx.elementId);
  const reportSelection = useSelectedReportContext();

  const divRef = React.useRef<HTMLDivElement>(null);
  const [fileType, setFileType] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [fileSelectAnchor, setFileSelectAnchor] =
    React.useState<HTMLDivElement | null>(null);
  const { data: topElement, isLoading: isDataLoading } = useElement(rootItem?.topelement);

  React.useEffect(() => {
    reportSelection.clearReport();
  }, [ctx.elementId]);

  if (topElementsLoading) {
    return (
      <>
        <Loader />
      </>
    );
  }
  return (
    <Page isLoading={topElementsLoading} Toolbar={ReportsToolbar}>
      <StackView gap="0px">
        <div></div>
        <RowView>
          <TreeViewLeftPanel>
            <ElementTreeView
              selectedItem={rootItem}
              items={buildTree(topElems) || []}              
              onSelected={(item) => {
                const userHasAccess = item.accesslevel !== "-1";
                link(userHasAccess ? { meaId: item.scorecardid }:{});
              }}
            />
          </TreeViewLeftPanel>
          <WhiteBgArea>
            <h2>{getTranslatedAttribute(topElement, "Name", ctx.language)}</h2>
            <MeasureGridArea minWidth="calc(100vw - 600px )">
              {rootItem ? <DivisionReport item={rootItem} /> : null}
            </MeasureGridArea>
          </WhiteBgArea>
          <div>
            <SideBar>
              <ScopeCard
                title={t("reports.Scope")}
                subtitle={t("reports.OK?")}
                onRemove={() => {
                  reportSelection.clearReport();
                }}
                showClearBtn
                selectedReport={reportSelection.report}
                selectedReportName={getTranslatedAttribute(
                  topElement,
                  "Name",
                  ctx.language
                )}
              />

              {reportSelection.report ? (
                <CollapseCard title={t("reports.Options")} isOpen>
                  <div>{t("reports.Select Period for the Report")}</div>
                  <PeriodSelector />
                  <LaunchReport
                    title={t("reports.Launch Report")}
                    onClick={() => {
                      setFileSelectAnchor(divRef.current);
                    }}
                  />{" "}
                </CollapseCard>
              ) : null}
              <div ref={divRef} style={{ transform: "translateY(-30px)" }} />

              {fileSelectAnchor ? (
                <>
                  <ExportFileSelectionPopUp
                    onClose={() => {
                      setFileSelectAnchor(null);
                    }}
                    handleClick={(value) => {
                      setFileType(value);
                      setFileSelectAnchor(null);
                      setIsExporting(true);
                    }}
                    anchorEl={fileSelectAnchor}
                  />
                </>
              ) : null}

              {isExporting && topElement && fileType ? (
                <StrategyReport
                  item={topElement}
                  filetype={fileType!}
                  onClose={() => {
                    setIsExporting(false);
                  }}
                />
              ) : null}
            </SideBar>
          </div>
        </RowView>
      </StackView>
    </Page>
  );
};

export default ReportTemplates;
